import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	MenuItem,
	Paper,
	Stack,
	Switch,
	TextField,
	Typography
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import { useEffect, useState } from "react";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import { AddCircleOutline, Delete, Science } from "@mui/icons-material";
import { useSnackbar } from "notistack";

interface BurntPattern {
	id: number
	pattern: string
	match_type: "substring" | "regex"
	is_active: boolean
	note: string
	created_on: number
}

interface PageData {
	patterns: BurntPattern[]
	total_count: number
	active_count: number
}

interface TestResult {
	is_burnt: boolean
	normalized_text: string
	matched_pattern: { id: number, pattern: string, match_type: string } | null
	active_pattern_count: number
}

const SAMPLE_BOUNCE =
	"550-5.7.1 [2402:1f00:8000:217::1 19] Gmail has detected that this message 550-5.7.1 is likely " +
	"suspicious due to the very low reputation of the sending 550-5.7.1 domain. To best protect our " +
	"users from spam, the message has been 550-5.7.1 blocked.";

export default function AdminBurntPatterns() {
	const { enqueueSnackbar } = useSnackbar();

	const [pageData, setPageData] = useState<PageData>();
	const [actionsDisabled, setActionsDisabled] = useState(false);

	const [openAddDialog, setOpenAddDialog] = useState(false);
	const [newPattern, setNewPattern] = useState("");
	const [newMatchType, setNewMatchType] = useState<"substring" | "regex">("substring");
	const [newNote, setNewNote] = useState("");

	const [openTestDialog, setOpenTestDialog] = useState(false);
	const [testText, setTestText] = useState("");
	const [testResult, setTestResult] = useState<TestResult | null>(null);

	const pageDataQuery = useQuery({
		queryKey: ["burntPatternsPageData"],
		queryFn: () => authenticateAndFetchData("/admin/burnt-patterns/"),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	})

	useEffect(() => {
		if (pageDataQuery.data) {
			setPageData(pageDataQuery.data.data);
		}
	}, [pageDataQuery.data]);

	const addPatternMutation = useMutation({
		mutationKey: ["addBurntPattern"],
		mutationFn: (data: { pattern: string, match_type: string, note: string }) =>
			authenticateAndPostData("/admin/burnt-patterns/add/", data),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			pageDataQuery.refetch().then();
			setOpenAddDialog(false);
			setNewPattern("");
			setNewNote("");
			setNewMatchType("substring");
			enqueueSnackbar("Pattern added", { variant: "success" });
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data?.message || "Failed to add pattern", { variant: "error" });
		}
	});

	const updatePatternMutation = useMutation({
		mutationKey: ["updateBurntPattern"],
		mutationFn: (data: { id: number, is_active: boolean }) =>
			authenticateAndPostData("/admin/burnt-patterns/update/", data),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			pageDataQuery.refetch().then();
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data?.message || "Failed to update pattern", { variant: "error" });
		}
	});

	const deletePatternMutation = useMutation({
		mutationKey: ["deleteBurntPattern"],
		mutationFn: (id: number) => authenticateAndPostData("/admin/burnt-patterns/delete/", { id }),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			pageDataQuery.refetch().then();
			enqueueSnackbar("Pattern deleted", { variant: "success" });
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data?.message || "Failed to delete pattern", { variant: "error" });
		}
	});

	const testPatternMutation = useMutation({
		mutationKey: ["testBurntPattern"],
		mutationFn: (bounce_text: string) =>
			authenticateAndPostData("/admin/burnt-patterns/test/", { bounce_text }),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (response) => {
			setTestResult(response.data as TestResult);
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data?.message || "Failed to test bounce", { variant: "error" });
		}
	});

	useEffect(() => {
		setActionsDisabled(
			addPatternMutation.isPending ||
			updatePatternMutation.isPending ||
			deletePatternMutation.isPending
		)
	}, [addPatternMutation.isPending, updatePatternMutation.isPending, deletePatternMutation.isPending]);

	const columns: GridColDef<BurntPattern>[] = [
		{
			field: "pattern",
			headerName: "Pattern",
			flex: 2,
			minWidth: 280,
			renderCell: (params) => (
				<Typography variant="body2" fontFamily="monospace">
					{params.value}
				</Typography>
			)
		},
		{
			field: "match_type",
			headerName: "Match Type",
			minWidth: 130,
			renderCell: (params) => (
				<Chip label={params.value} size="small" variant="outlined" />
			)
		},
		{
			field: "is_active",
			headerName: "Active",
			minWidth: 110,
			sortable: true,
			renderCell: (params) => (
				<Switch
					checked={params.value}
					disabled={actionsDisabled}
					onChange={(e) => updatePatternMutation.mutate({
						id: params.row.id,
						is_active: e.target.checked,
					})}
				/>
			)
		},
		{
			field: "note",
			headerName: "Note",
			flex: 1,
			minWidth: 200,
		},
		{
			field: "created_on",
			headerName: "Added On",
			minWidth: 150,
			renderCell: (params) => (
				<Typography variant="body2">
					{new Date(params.value).toLocaleDateString()}
				</Typography>
			)
		},
		{
			field: "actions",
			headerName: "Actions",
			filterable: false,
			sortable: false,
			minWidth: 100,
			renderCell: (params) => (
				<IconButton
					color="error"
					size="small"
					onClick={() => handleDelete(params.row.id)}
					disabled={actionsDisabled}
				>
					<Delete />
				</IconButton>
			)
		}
	];

	function handleDelete(id: number) {
		if (window.confirm("Delete this pattern? Bounces matching it will no longer mark a domain as burnt.")) {
			deletePatternMutation.mutate(id);
		}
	}

	function handleSubmitAdd() {
		if (!newPattern.trim()) {
			enqueueSnackbar("Pattern cannot be empty", { variant: "error" });
			return;
		}
		addPatternMutation.mutate({
			pattern: newPattern.trim(),
			match_type: newMatchType,
			note: newNote.trim(),
		});
	}

	function handleOpenTest() {
		setTestResult(null);
		setTestText("");
		setOpenTestDialog(true);
	}

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (pageDataQuery.isLoading) {
		return <PageLoading />
	}

	if (pageDataQuery.isError) {
		return <PageDataErrorHandler error={pageDataQuery.error} />
	}

	return (
		<Box>
			<Stack direction="column" spacing={3}>
				<Typography variant="h4">
					Burnt Domain Patterns
				</Typography>

				<Typography color="text.secondary">
					A bounce matching any active pattern below marks its sending domain as burnt, which
					pauses warmup and campaigns according to the burnt-domain feature flags. Patterns are
					matched case-insensitively against normalized bounce text.
				</Typography>

				{pageData && pageData.active_count === 0 && (
					<Alert severity="warning">
						No active patterns. Burnt domain detection is currently switched off entirely - no
						bounce will ever flag a domain, regardless of the feature flags.
					</Alert>
				)}

				<Alert severity="info">
					Providers repeat the SMTP status code on every wrapped line, so raw bounce text reads
					<Box component="span" fontFamily="monospace"> ...of the sending 550-5.7.1 domain</Box>.
					Matching runs after those codes are stripped, so write patterns as plain prose
					(<Box component="span" fontFamily="monospace">very low reputation of the sending domain</Box>).
					Use <b>Test a bounce</b> to confirm before relying on a new pattern.
				</Alert>

				{pageData && (
					<Stack direction="row" spacing={2}>
						<Chip label={`Total: ${pageData.total_count}`} color="primary" />
						<Chip
							label={`Active: ${pageData.active_count}`}
							color={pageData.active_count === 0 ? "error" : "success"}
						/>
					</Stack>
				)}

				<Stack direction="row" spacing={2}>
					<Button
						variant="contained"
						startIcon={<AddCircleOutline />}
						onClick={() => setOpenAddDialog(true)}
						disabled={actionsDisabled}
					>
						Add Pattern
					</Button>
					<Button
						variant="outlined"
						startIcon={<Science />}
						onClick={handleOpenTest}
					>
						Test a bounce
					</Button>
				</Stack>

				<Divider />

				{pageData && (
					<DataGrid
						rows={pageData.patterns}
						columns={columns}
						checkboxSelection={false}
						disableRowSelectionOnClick
						autoHeight
						pageSizeOptions={[25, 50, 100]}
						initialState={{
							pagination: { paginationModel: { pageSize: 25 } },
						}}
					/>
				)}
			</Stack>

			{/* Add pattern dialog */}
			<Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Add Burnt Domain Pattern</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							label="Pattern"
							value={newPattern}
							onChange={(e) => setNewPattern(e.target.value)}
							placeholder="very low reputation of the sending domain"
							helperText="Plain prose, no SMTP status codes. Case-insensitive."
							fullWidth
							multiline
							minRows={2}
						/>
						<TextField
							select
							label="Match Type"
							value={newMatchType}
							onChange={(e) => setNewMatchType(e.target.value as "substring" | "regex")}
							helperText="Substring is a literal contains check. Regex is a Python regular expression."
							fullWidth
						>
							<MenuItem value="substring">Substring</MenuItem>
							<MenuItem value="regex">Regex</MenuItem>
						</TextField>
						<TextField
							label="Note"
							value={newNote}
							onChange={(e) => setNewNote(e.target.value)}
							placeholder="Gmail 5.7.1 sending-domain reputation block"
							helperText="Optional. Which provider / rejection this covers."
							fullWidth
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
					<Button
						onClick={handleSubmitAdd}
						variant="contained"
						disabled={addPatternMutation.isPending}
					>
						Add Pattern
					</Button>
				</DialogActions>
			</Dialog>

			{/* Test dialog */}
			<Dialog open={openTestDialog} onClose={() => setOpenTestDialog(false)} maxWidth="md" fullWidth>
				<DialogTitle>Test a bounce message</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<Typography variant="body2" color="text.secondary">
							Paste raw bounce text to see whether it would flag a domain as burnt. This only
							checks the active patterns - no domain is modified.
						</Typography>
						<TextField
							label="Bounce text"
							value={testText}
							onChange={(e) => setTestText(e.target.value)}
							fullWidth
							multiline
							minRows={5}
						/>
						<Stack direction="row" spacing={2}>
							<Button
								variant="contained"
								onClick={() => testPatternMutation.mutate(testText)}
								disabled={!testText.trim() || testPatternMutation.isPending}
							>
								{testPatternMutation.isPending ? "Checking..." : "Check"}
							</Button>
							<Button onClick={() => setTestText(SAMPLE_BOUNCE)}>
								Load Gmail sample
							</Button>
						</Stack>

						{testResult && (
							<Stack spacing={2}>
								<Alert severity={testResult.is_burnt ? "error" : "success"}>
									{testResult.is_burnt
										? "This bounce WOULD mark the sending domain as burnt."
										: "This bounce would NOT mark the sending domain as burnt."}
								</Alert>

								{testResult.matched_pattern && (
									<Typography variant="body2">
										Matched pattern:{" "}
										<Box component="span" fontFamily="monospace">
											{testResult.matched_pattern.pattern}
										</Box>{" "}
										({testResult.matched_pattern.match_type})
									</Typography>
								)}

								<Box>
									<Typography variant="subtitle2" gutterBottom>
										Normalized text the matcher actually sees
									</Typography>
									<Paper variant="outlined" sx={{ p: 1.5 }}>
										<Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: "break-word" }}>
											{testResult.normalized_text || "(empty)"}
										</Typography>
									</Paper>
								</Box>
							</Stack>
						)}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenTestDialog(false)}>Close</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
